import * as z from "zod"
import { Database } from "@/integrations/supabase/types"

export type Opportunity = Database["public"]["Tables"]["opportunities"]["Row"]

export const EventFormatType = {
  IN_OFFICE: "In-Office",
  VIRTUAL: "Virtual",
  OUTDOOR: "Outdoor",
  KIOSK: "Kiosk",
  HYBRID: "Hybrid",
  ONLINE: "Online",
} as const

export type EventFormatType = typeof EventFormatType[keyof typeof EventFormatType]

export const EventDurationType = {
  MINS_30: "30 mins",
  MINS_60: "60 mins",
  MINS_90: "90 mins",
  MINS_120: "120 mins",
  MINS_180: "180 mins",
  HALF_DAY: "Half day",
  FULL_DAY: "Full day",
} as const

export type EventDurationType = typeof EventDurationType[keyof typeof EventDurationType]

export const catalogFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  prerequisites: z.string().min(1, "Prerequisites are required"),
  timeline: z.array(z.string()).min(1, "Timeline is required"),
  eventCost: z.coerce.number().min(0, "Cost must be positive"),
  minVolunteers: z.coerce.number().min(1, "Minimum volunteers must be at least 1"),
  maxVolunteers: z.coerce.number({
    required_error: "Maximum number of volunteers is required",
    invalid_type_error: "Maximum volunteers must be a number",
  }).min(1, {
    message: "Maximum volunteers must be at least 1",
  }).refine((val) => val > 0, {
    message: "Maximum volunteers must be greater than 0",
  }),
  volunteerOutput: z.string().min(1, "Volunteer output is required"),
  images: z.array(z.string()).min(1, "At least one image is required"),
  format: z.enum([
    EventFormatType.IN_OFFICE,
    EventFormatType.VIRTUAL,
    EventFormatType.OUTDOOR,
    EventFormatType.KIOSK,
    EventFormatType.HYBRID,
    EventFormatType.ONLINE
  ], {
    required_error: "Please select a location format"
  }),
  duration: z.enum([
    EventDurationType.MINS_30,
    EventDurationType.MINS_60,
    EventDurationType.MINS_90,
    EventDurationType.MINS_120,
    EventDurationType.MINS_180,
    EventDurationType.HALF_DAY,
    EventDurationType.FULL_DAY
  ], {
    required_error: "Please select a duration"
  }),
  format_details: z.string().optional(),
  whats_included: z.string().min(1, "What's included is required"),
  faqs: z.string().min(1, "FAQs are required"),
  waiver_forms: z.string().min(1, "Waiver forms are required"),
  terms_and_conditions: z.string().min(1, "Terms and conditions are required"),
  cancellation_policy: z.enum(["full_refund", "partial_refund", "non_refundable"], {
    required_error: "Please select a cancellation policy"
  }),
  cancellation_terms: z.string().min(1, "Cancellation terms are required"),
  host_included: z.boolean({
    required_error: "Please specify if host is included"
  }),
}).refine((data) => data.maxVolunteers >= data.minVolunteers, {
  message: "Maximum volunteers must be greater than or equal to minimum volunteers",
  path: ["maxVolunteers"],
})

export type CatalogFormData = z.infer<typeof catalogFormSchema>

// Helper function to convert arrays to comma-separated strings
export const arrayToString = (arr: string[] | undefined | null): string => {
  if (!arr || !Array.isArray(arr)) return ''
  return arr.join('\n')
}

// Helper function to convert comma-separated strings to arrays
export const stringToArray = (str: string | undefined | null): string[] => {
  if (!str) return []
  return str.split('\n').map(s => s.trim()).filter(Boolean)
}

// Helper function to convert timeline string to array
export function parseTimeline(text: string): string[] {
  if (!text) return []
  return text.split('\n')
    .map(line => line.trim())
    .filter(Boolean)
}

// Helper function to convert timeline array to string
export const timelineToString = (timeline: string[] | undefined): string => {
  if (!timeline || !Array.isArray(timeline)) return ''
  return timeline.join('\n')
}